import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import styles from './Legal.module.scss';

const LAST_UPDATED = 'July 8, 2026';
const COMPANY_NAME = 'FOTH, also known as Friends of the House';
const APP_URL = 'https://www.wearefoth.com';
const CONTACT_EMAIL = 'admin@wearefoth.com';

const Section = ({ id, title, children }) => (
  <section id={id} className={styles.section}>
    <h2 className={styles.sectionTitle}>{title}</h2>
    <div className={styles.sectionBody}>{children}</div>
  </section>
);

const TermsOfService = () => {
  const { t } = useTranslation('legal');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const tocItems = [
    ['#acceptance', t('terms.sections.acceptance.title', '1. Acceptance of Terms')],
    ['#accounts', t('terms.sections.accounts.title', '2. User Accounts & Responsibilities')],
    ['#instagram', t('terms.sections.meta_api.title', '3. Instagram & Meta API Compliance')],
    ['#collabs', t('terms.sections.collabs.title', '4. Collaborations & Milestone Contracts')],
    ['#payments', t('terms.sections.payments.title', '5. Payments, Fees & Escrow Protection')],
    ['#cancellation', t('terms.sections.cancellation.title', '6. Cancellation & Refunds')],
    ['#conduct', t('terms.sections.conduct.title', '7. Prohibited Conduct')],
    ['#ip-content', t('terms.sections.intellectual_property.title', '8. Intellectual Property')],
    ['#liability', t('terms.sections.disclaimer.title', '9. Limitation of Liability')],
    ['#governing-law', t('terms.sections.governing_law.title', '10. Governing Law & Dispute Resolution')],
  ];

  return (
    <div className={styles.legalPage}>
      <div className={styles.legalHero}>
        <div className={styles.container}>
          <div className={styles.heroBadge}>{t('common.badge', 'Legal')}</div>
          <h1 className={styles.heroTitle}>{t('terms.title', 'Terms of Service')}</h1>
          <p className={styles.heroSub}>
            {t('common.last_updated', { date: LAST_UPDATED, defaultValue: `Last updated: ${LAST_UPDATED}` })}
          </p>
          <p className={styles.heroDesc}>
            {t('terms.desc', `These Terms of Service ("Terms") govern your access to and use of the FOTH website, services, and applications operated by ${COMPANY_NAME}.`)}
          </p>
        </div>
      </div>

      <div className={styles.legalLayout}>
        <aside className={styles.toc}>
          <div className={styles.tocLabel}>{t('common.table_of_contents', 'Table of Contents')}</div>
          <nav>
            {tocItems.map(([href, label]) => (
              <a key={href} href={href} className={styles.tocLink}>{label}</a>
            ))}
          </nav>
        </aside>

        <main className={styles.legalMain}>
          <Section id="acceptance" title={t('terms.sections.acceptance.title', '1. Acceptance of Terms')}>
            <p>{t('terms.sections.acceptance.content', 'By accessing or using the platform, you confirm that you are at least 18 years old and legally competent to enter into these binding Terms.')}</p>
          </Section>

          <Section id="accounts" title={t('terms.sections.accounts.title', '2. User Accounts & Responsibilities')}>
            <p>{t('terms.sections.accounts.content', 'You must provide accurate, current information during registration. You are responsible for safeguarding your credentials and all activity under your account.')}</p>
          </Section>

          <Section id="instagram" title={t('terms.sections.meta_api.title', '3. Instagram & Meta API Compliance')}>
            <p>{t('terms.sections.meta_api.content', 'Creators connecting their Instagram accounts grant FOTH limited access to read profile metadata and post insights for verification. Users agree not to attempt to manipulate engagement metrics or submit fraudulent deliverables.')}</p>
          </Section>

          <Section id="collabs" title={t('terms.sections.collabs.title', '4. Collaborations & Milestone Contracts')}>
            <p>{t('terms.sections.collabs.content', 'Collabs entered into through FOTH represent binding commercial agreements between brands and creators. Both parties agree to adhere to agreed deliverables, review timelines, and compensation terms.')}</p>
          </Section>

          <Section id="payments" title={t('terms.sections.payments.title', '5. Payments, Fees & Escrow Protection')}>
            <p>{t('terms.sections.payments.content', 'All paid collabs and subscription fees are processed securely via Stripe. Funds for paid collaborations are held in protected accounts until deliverables are validated according to the platform refund and review window policies.')}</p>
          </Section>

          <Section id="cancellation" title={t('terms.sections.cancellation.title', '6. Cancellation & Refunds')}>
            <p>{t('terms.sections.cancellation.content', 'Refund requests and cancellations are governed by FOTH\'s Refund Policy. Disputed milestones are subject to administrative review.')}</p>
          </Section>

          <Section id="conduct" title={t('terms.sections.conduct.title', '7. Prohibited Conduct')}>
            <p>{t('terms.sections.conduct.content', 'Users may not engage in harassment, copyright infringement, dissemination of malicious software, or unauthorized scraping of platform data.')}</p>
          </Section>

          <Section id="ip-content" title={t('terms.sections.intellectual_property.title', '8. Intellectual Property')}>
            <p>{t('terms.sections.intellectual_property.content', 'FOTH owns all rights in its platform interface, proprietary algorithms, and brand assets. Content creators retain ownership of their original creative assets, subject to licenses granted to collaboration sponsors.')}</p>
          </Section>

          <Section id="liability" title={t('terms.sections.disclaimer.title', '9. Limitation of Liability')}>
            <p>{t('terms.sections.disclaimer.content', 'To the maximum extent permitted by applicable law, FOTH shall not be liable for indirect, incidental, or consequential damages resulting from platform use.')}</p>
          </Section>

          <Section id="governing-law" title={t('terms.sections.governing_law.title', '10. Governing Law & Dispute Resolution')}>
            <p>{t('terms.sections.governing_law.content', 'These Terms are governed by the laws of the State of Delaware, without regard to its conflict of law principles.')}</p>
            <p>
              <strong>Contact:</strong>{' '}
              <a href={`mailto:${CONTACT_EMAIL}`} className={styles.inlineLink}>{CONTACT_EMAIL}</a>
            </p>
          </Section>
        </main>
      </div>
    </div>
  );
};

export default TermsOfService;
